import { isRecord } from '@/shared/utils/apiResponse';

import type { TipoBlocoComNo } from '../types/editor';
import { PRAZO_PADRAO_CONCLUSO_DU, TEXTO_PADRAO_CONCLUSO } from '../catalogo/blocos';
import { intimaORequerente } from '../catalogo/destinatarioDaIntimacao';
import { emailsCitacaoDoNode } from '../catalogo/emailsCitacao';
import { lerAvisoAtraso, TIPOS_ESPERA_COM_VENCIMENTO } from '../catalogo/esperaVencimento';
import { variavelDoCampo } from '../catalogo/prazoVigiado';

type Dados = Record<string, unknown>;
type Parametros = Record<string, unknown>;

const ehTexto = (valor: unknown): valor is string => typeof valor === 'string';
const ehTextoOuNulo = (valor: unknown): valor is string | null =>
  typeof valor === 'string' || valor === null;
const ehNumero = (valor: unknown): valor is number =>
  typeof valor === 'number' && Number.isFinite(valor);
const ehBooleano = (valor: unknown): valor is boolean => typeof valor === 'boolean';
const ehListaDeTextos = (valor: unknown): valor is string[] =>
  Array.isArray(valor) && valor.every(ehTexto);

/** `data[origem]` como `parametros[destino]`, só quando o valor tem o formato que o compilador emite. */
function campo(
  data: Dados,
  origem: string,
  guarda: (valor: unknown) => boolean,
  destino: string = origem,
): Parametros {
  const valor = data[origem];
  return guarda(valor) ? { [destino]: valor } : {};
}

/** O `node.data` de um bloco salvo com o tipo em `data.tipo` (sem `tipoBloco`) não tem o parâmetro `tipo`. */
function semOTipoDoBloco(data: Dados): Dados {
  if ((data.tipoBloco ?? null) !== null) return data;
  const { tipo: _tipoDoBloco, ...resto } = data;
  return resto;
}

// O que está no `node.data` e não é parâmetro do bloco: a etapa (que o compilador acrescenta) e o
// tipo, o rótulo, a ordem e os parâmetros do formato antigo do editor.
export const CHAVES_FORA_DOS_PARAMETROS: ReadonlySet<string> = new Set([
  'tipoBloco',
  'etapaId',
  'etapaNome',
  'etapaOrdem',
  'label',
  'ordem',
  'parametros',
]);

function regrasDaCondicao(data: Dados): Parametros {
  const conector =
    data.logicOperator === 'AND' || data.logicOperator === 'OR'
      ? { conector: data.logicOperator }
      : {};
  if (!Array.isArray(data.conditions)) return conector;
  const regras = data.conditions
    .filter(isRecord)
    .map((c) => ({ id: c.id, campo: c.variable, operador: c.operator, valor: c.value }));
  return { ...conector, regras };
}

/**
 * `pausar` volta sempre que o nó o traz, com ou sem variável: o executor trata ausência como modo
 * pausa e a execução fica parada até o vencimento, descartando o que chega no meio
 * (ARB-202609-000012). `tipoPrazoId: null` é o prazo manual (o compilador o emite sempre) e também
 * volta, para não herdar o tipo do formato antigo.
 */
function parametrosDoIniciarPrazo(data: Dados): Parametros {
  const savePrazoIdToVariable = variavelDoCampo(data.savePrazoIdToVariable);
  return {
    ...campo(data, 'tipoPrazoId', ehTextoOuNulo),
    ...campo(data, 'diasUteis', ehNumero),
    ...campo(data, 'nomePrazo', ehTexto),
    ...campo(data, 'pausar', ehBooleano),
    ...(savePrazoIdToVariable ? { savePrazoIdToVariable } : {}),
  };
}

/**
 * O executor lê os eventos de `eventTypes`; a tela, de `eventos`. Sem a tradução, "Prazo vigiado"
 * sumia da tela (só aparece com "Prazo vencido") e o nó saía com `eventTypes: []`, que o executor
 * recusa; por isso `eventTypes` vazio não sobrescreve o formato antigo. O `prazoId` e o
 * `pendenciaId` (`'{{x}}'`) voltam como o nome puro que a tela usa; o literal que não é template
 * volta como está (D-04 do core#2249).
 */
function parametrosDaEspera(data: Dados): Parametros {
  const eventos = Array.isArray(data.eventTypes) ? data.eventTypes.filter(ehTexto) : [];
  const pendenciaId = variavelDoCampo(data.pendenciaId);
  const prazoVigiado = variavelDoCampo(data.prazoId);
  return {
    ...(eventos.length > 0 ? { eventos } : {}),
    ...(pendenciaId ? { pendenciaId } : {}),
    ...(prazoVigiado ? { prazoVigiado } : {}),
  };
}

/**
 * `tipo` e `titulo` são obrigatórios no executor e `savePendenciaIdToVariable` é o par do
 * `pendenciaId` da espera: sem os três, um fluxo com a pendência vigiada voltaria do save com a
 * espera apontando para uma variável que ninguém mais grava. `bloqueiaAvanco` volta como está:
 * sem ele o bloco nascia do default da tela (`true`) e invertia a pendência não bloqueante
 * (core#2249, EDGE-16).
 */
function parametrosDaPendencia(data: Dados): Parametros {
  const savePendenciaIdToVariable = variavelDoCampo(data.savePendenciaIdToVariable);
  return {
    ...campo(data, 'tipo', ehTexto),
    ...campo(data, 'titulo', ehTexto),
    ...campo(data, 'descricao', ehTexto),
    ...campo(data, 'solicitante', ehTexto, 'responsavelTipo'),
    ...campo(data, 'destinatario', ehTexto),
    ...campo(data, 'prazoDiasUteis', ehNumero),
    ...campo(data, 'requerValidacao', ehBooleano),
    ...campo(data, 'bloqueiaAvanco', ehBooleano),
    ...(savePendenciaIdToVariable ? { savePendenciaIdToVariable } : {}),
  };
}

/**
 * A tela escolhe uma visibilidade só e o compilador a embrulha numa lista. Lista com mais de um
 * papel não cabe no campo e não volta (o save seguinte a regravaria com um papel só).
 */
function visibilidadeDoDocumento(data: Dados): Parametros {
  const { visivelPara } = data;
  return ehListaDeTextos(visivelPara) && visivelPara.length === 1
    ? { visivelPara: visivelPara[0] }
    : {};
}

function parametrosDaCitacao(data: Dados): Parametros {
  const emailsCitacao = emailsCitacaoDoNode(data);
  return {
    ...campo(data, 'fluxoCitacaoId', ehTexto),
    ...(emailsCitacao ? { emailsCitacao } : {}),
    ...campo(data, 'janelaReversaoMinutos', ehNumero),
  };
}

/** O bloco sem tradução (`default` do compilador) grava os parâmetros como estão. */
function parametrosSemTraducao(data: Dados): Parametros {
  return Object.fromEntries(
    Object.entries(data).filter(([chave]) => !CHAVES_FORA_DOS_PARAMETROS.has(chave)),
  );
}

/**
 * Um inverso por tipo, um a um com os `case` de `construirDataNode`. O `switch` não tem `default`
 * de propósito: tipo novo sem caminho de volta não compila.
 */
function parametrosDoTipo(tipo: TipoBlocoComNo, data: Dados): Parametros {
  switch (tipo) {
    case 'CONDICAO':
      return regrasDaCondicao(data);
    case 'MUDAR_STATUS':
      return campo(data, 'novoStatus', ehTextoOuNulo, 'statusDestino');
    case 'SET_SITUACAO':
      return {
        ...campo(data, 'situacaoKeys', ehListaDeTextos),
        ...campo(data, 'desativarAnteriores', ehBooleano),
      };
    case 'CRIAR_ANDAMENTO':
      return {
        ...campo(data, 'descricao', ehTexto, 'texto'),
        ...campo(data, 'categoriaModelo', ehTexto, 'tipo'),
        ...campo(data, 'modeloId', ehTexto),
      };
    case 'CRIAR_PENDENCIA':
      return parametrosDaPendencia(data);
    case 'GERAR_DOCUMENTO':
      return {
        ...campo(data, 'categoria', ehTexto),
        ...campo(data, 'templateId', ehTexto),
        ...campo(data, 'publicarAutomatico', ehBooleano),
        ...visibilidadeDoDocumento(data),
      };
    case 'PUBLICAR_DECURSO':
      return campo(data, 'templateId', ehTexto);
    case 'PUBLICAR_TRANSITO':
      return {
        ...campo(data, 'motivoEncerramento', ehTexto),
        ...campo(data, 'templateId', ehTexto),
        ...campo(data, 'lexiFlowId', ehTexto),
      };
    case 'FINALIZAR_PROCESSO':
      return {
        ...campo(data, 'statusFinal', ehTexto),
        ...campo(data, 'motivoEncerramento', ehTexto),
        ...campo(data, 'motivoEncerramentoTexto', ehTexto),
        ...campo(data, 'fecharPendencias', ehBooleano),
      };
    case 'AGUARDAR_EMBARGOS':
      return campo(data, 'prazoDiasUteis', ehNumero, 'diasUteis');
    case 'SORTEAR_ARBITRO':
      return {
        ...campo(data, 'algoritmo', ehTexto),
        ...campo(data, 'excluirArbitroIds', ehListaDeTextos),
      };
    case 'EXIGIR_TERMO_IMPARCIALIDADE':
      return {
        ...campo(data, 'templateIdTermoImparcialidade', ehTexto),
        ...campo(data, 'templateIdTermoArbitragem', ehTexto),
      };
    case 'CONCLUSO':
      return {
        ...campo(data, 'descricao', ehTexto),
        ...campo(data, 'prazoDiasUteis', ehNumero),
      };
    case 'INTIMAR_SENTENCA':
      // O check do requerente volta sempre: sem ele, o save seguinte desmarcaria o requerente.
      return {
        intimarRequerente: intimaORequerente(data),
        ...campo(data, 'templateId', ehTexto),
        ...campo(data, 'templateVersaoId', ehTexto),
        ...campo(data, 'assuntoEmail', ehTexto),
        ...campo(data, 'modeloEmailId', ehTexto),
        ...campo(data, 'lexiFlowId', ehTexto),
        ...campo(data, 'gerarDocumentoPdf', ehBooleano),
      };
    case 'CITACAO':
      return parametrosDaCitacao(data);
    case 'COBRAR_ASAAS':
      return campo(data, 'vencimentoDiasUteis', ehNumero);
    case 'ENVIAR_EMAIL':
    case 'ENVIAR_WHATSAPP':
      return {
        ...campo(data, 'para', ehTexto),
        ...campo(data, 'modeloComunicacaoId', ehTextoOuNulo),
      };
    case 'NOTIF_INAPP':
      return {
        ...campo(data, 'para', ehTexto),
        ...campo(data, 'modeloNotificacaoId', ehTextoOuNulo),
      };
    case 'ENVIAR_AR':
      return {
        ...campo(data, 'modeloCartaId', ehTextoOuNulo),
        ...campo(data, 'somenteSeEmailNaoAberto', ehBooleano),
        ...campo(data, 'prazoDiasUteis', ehNumero),
      };
    case 'INICIAR_PRAZO':
      return parametrosDoIniciarPrazo(data);
    case 'AGUARDAR_EVENTO':
      return parametrosDaEspera(data);
    case 'AGUARDAR_VISUALIZACAO':
      // A espera que libera na primeira leitura (intimação da sentença): sem a volta, o save
      // seguinte voltaria a exigir a leitura de todas as partes.
      return campo(data, 'bastaUma', ehBooleano);
    case 'AGUARDAR_CITACAO':
    case 'REGISTRAR_CONSUMO_CONVENIO':
      return {};
    case 'DETECTAR_MANIFESTACAO':
      return parametrosSemTraducao(data);
  }
}

/**
 * O vencimento das três esperas. `timeoutDiasUteis` volta nos dois modos: em SEGUIR_RAMO o
 * compilador só emite o `timeoutNodeId` com o prazo, então sem a volta a espera perderia o
 * vencimento no save seguinte (o que a guarda de ligações acusava como caminho mudado).
 */
function vencimentoDoNode(tipo: TipoBlocoComNo, data: Dados): Parametros {
  if (!TIPOS_ESPERA_COM_VENCIMENTO.has(tipo)) return {};
  const prazo = campo(data, 'timeoutDiasUteis', ehNumero);
  if (data.modoVencimento !== 'AVISAR_E_AGUARDAR') return prazo;
  return {
    modoVencimento: 'AVISAR_E_AGUARDAR',
    avisoAtraso: lerAvisoAtraso(data.avisoAtraso),
    ...prazo,
  };
}

// O executor `arbitral.concluso` roda o nó sem `descricao`/`prazoDiasUteis` com o texto padrão e
// 15 DU. Sem isto, a barra lateral mostraria esses valores e a validação recusaria o bloco por
// não tê-los; e o save seguinte os grava explícitos, que é o que o core já executava.
const PADRAO_DO_CONCLUSO_AUSENTE: Parametros = {
  descricao: TEXTO_PADRAO_CONCLUSO,
  prazoDiasUteis: PRAZO_PADRAO_CONCLUSO_DU,
};

/**
 * Os parâmetros do bloco que o `node.data` descreve: o caminho de volta do compilador da régua
 * (`construirDataNode`), para o fluxo sem `metadata.editorSource`. Aí os parâmetros só existem no
 * `node.data`, que é onde o core os lê, e cada tipo os grava com nomes e formatos próprios
 * (`statusDestino` → `novoStatus`, `texto` → `descricao`, `diasUteis` → `prazoDiasUteis`...). Sem
 * a volta, o save seguinte pelo editor gravaria o bloco com o default da tela no lugar do que o
 * processo executa hoje. O que a volta não alcança fica a cargo da guarda do editor
 * (`TROCARIA_PARAMETROS`), que recusa o save em vez de gravar o default.
 *
 * Precedência (a mesma desde o #1315), da mais fraca para a mais forte:
 * 1. `data.parametros`, o formato antigo do editor;
 * 2. o inverso do tipo: o que o nó traz vence o formato antigo, porque é o que a execução de fato
 *    faz, `null` inclusive quando o compilador o emite. Chave ausente, ou num formato que o
 *    compilador não emite, não diz nada e deixa o formato antigo;
 * 3. o vencimento das esperas, comum aos três tipos.
 */
export function parametrosDoNode(tipo: TipoBlocoComNo, data: Dados): Parametros {
  const formatoAntigo = isRecord(data.parametros) ? data.parametros : {};
  const parametros = {
    ...formatoAntigo,
    ...parametrosDoTipo(tipo, semOTipoDoBloco(data)),
    ...vencimentoDoNode(tipo, data),
  };
  return tipo === 'CONCLUSO' ? { ...PADRAO_DO_CONCLUSO_AUSENTE, ...parametros } : parametros;
}
